// src/shared/hooks/useSidebarMenu.ts
// 사이드바 메뉴 상태 관리 훅 (리팩토링 + non-null assertion 제거)

import type { Feature, SDKType, SDKCategory } from '../../types/global.types';

import { useState, useEffect, useRef, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';

import { getLogger } from '@/lib/logger/logger';

import {
  getEnabledCategoriesByRegion,
  getPathToMenuIdByRegion,
  getDefaultExpandedCategories,
  getMenuIdToPathByRegion,
} from '../../config/menu.config';
import { useAppStore } from '../../store';
import {
  getSDKTypeByCategory,
  isEmptySDK,
  createVirtualFeature,
} from '../components/Sidebar/SidebarItem';
import {
  isValidSDKCategory,
  populateFeaturesMap,
  loadCategoryFeatures,
  calculateFeatureListHeight as calcHeight,
} from '../utils/sidebarMenuUtils';
import { throttle } from '../utils/throttle';

import { useRegion } from './useRegion';

const logger = getLogger('useSidebarMenu');

interface UseSidebarMenuProps {
  isOpen: boolean;
}

export const useSidebarMenu = ({ isOpen }: UseSidebarMenuProps) => {
  const { selectedFeature, setSelectedFeature, setCurrentSDK } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { isOverseas } = useRegion();

  // 확장된 카테고리 상태 (default로 HWP SDK 선택)
  const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>(
    getDefaultExpandedCategories(),
  );

  // 기능/카테고리 상태
  const [features, setFeatures] = useState<{ [key: string]: Feature }>({});
  const [categories, setCategories] = useState<SDKCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 사이드바 높이 계산용
  const [featureListHeight, setFeatureListHeight] = useState<number>(300);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // URL sync 초기화 여부 추적 (새로고침 시 카테고리 확장 문제 해결용)
  const urlSyncInitializedRef = useRef<boolean>(false);

  /** ---------- 공통 유틸 ---------- */

  // SDK 타입을 URL 경로로 매핑
  const getSDKPath = useCallback(
    (sdkType: string): string => {
      const pathMap = getMenuIdToPathByRegion(isOverseas);
      const basePath = pathMap[sdkType] ?? (isOverseas ? '/spreadsheet-sdk' : '/hwp-sdk');
      // 국내 EN → /en/, 해외 JA → /ja/, 그 외는 prefix 없음
      if (!isOverseas && i18n.language === 'en') return `/en${basePath}`;
      if (isOverseas && i18n.language === 'ja') return `/ja${basePath}`;
      return basePath;
    },
    [i18n.language, isOverseas],
  );

  // 선택된 카테고리만 확장
  const expandOnly = useCallback((categoryId: SDKType) => {
    setExpandedCategories(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        next[key] = key === categoryId;
      });
      return next;
    });
  }, []);

  // 모든 카테고리 닫기
  const collapseAll = useCallback(() => {
    setExpandedCategories(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => (next[key] = false));
      return next;
    });
  }, []);

  // 카테고리에서 첫 기능 자동 선택
  const selectFirstFeatureInCategory = useCallback(
    (categoryId: SDKType) => {
      const category = categories.find(cat => cat.id === categoryId);
      const firstFeature = category?.features?.[0];
      if (!firstFeature) return false;

      logger.info('Auto-selecting first feature:', {
        categoryId,
        featureId: firstFeature.id,
        title: firstFeature.title,
      });
      setSelectedFeature(firstFeature);
      return true;
    },
    [categories, setSelectedFeature],
  );

  // SDK별로 적절한 기능이 선택돼 있는지 보장 (HWP/HCELL 등 공통화)
  const ensureFeatureSelectedForSdk = useCallback(
    (sdkType: string) => {
      const currentCategory = selectedFeature?.category;
      if (currentCategory === sdkType) return;

      // 빈 SDK(docdiff, ocr)의 경우 가상 기능 생성
      if (isValidSDKCategory(sdkType) && isEmptySDK(sdkType)) {
        const virtualFeature = createVirtualFeature(sdkType);
        logger.info('Creating virtual feature for empty SDK:', {
          sdkType,
          virtualFeature,
        });
        setSelectedFeature(virtualFeature);
        return;
      }

      const picked = selectFirstFeatureInCategory(sdkType);
      if (!picked) logger.warn(`No features found for SDK: ${sdkType}`);
    },
    [selectedFeature?.category, selectFirstFeatureInCategory, setSelectedFeature],
  );

  /** ---------- 기능 목록 로드 ---------- */
  useEffect(() => {
    if (!isOpen) return; // 사이드바가 닫혀 있으면 로드하지 않음

    // ✅ Race condition prevention: Use a flag to cancel stale updates
    let isCancelled = false;

    logger.info('[useSidebarMenu] Loading features effect triggered:', {
      isOverseas,
      language: i18n.language,
      isOpen,
    });

    const loadFeatures = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const featuresMap: { [key: string]: Feature } = {};
        const enabledCategories = getEnabledCategoriesByRegion(isOverseas);

        logger.info('[useSidebarMenu] Enabled categories for region:', {
          isOverseas,
          categoryIds: enabledCategories.map(c => c.id),
        });

        const categoriesList: SDKCategory[] = await Promise.all(
          enabledCategories.map(category => loadCategoryFeatures(category, i18n.language)),
        );

        // featuresMap 적재
        for (const cat of categoriesList) {
          populateFeaturesMap(cat.features, featuresMap);
        }

        // ✅ Only update state if this effect hasn't been cancelled
        if (isCancelled) {
          logger.info('[useSidebarMenu] Request cancelled, skipping state update');
          return;
        }

        logger.info('[useSidebarMenu] Setting categories state:', {
          categoryCount: categoriesList.length,
          categoryIds: categoriesList.map(c => c.id),
          featureCount: Object.keys(featuresMap).length,
        });

        setCategories(categoriesList);
        setFeatures(featuresMap);

        logger.info('[useSidebarMenu] Categories state updated');
      } catch (err) {
        if (isCancelled) {
          logger.info('[useSidebarMenu] Request cancelled, skipping error handling');
          return;
        }

        const msg = err instanceof Error ? err.message : '기능 로드에 실패했습니다';
        setError(msg);
        logger.error('기능 로드 실패:', err);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadFeatures().catch(() => {
      // Error handled internally in loadFeatures
    });

    // ✅ Cleanup: Cancel this effect when dependencies change
    return () => {
      logger.info('[useSidebarMenu] Cleaning up - cancelling stale requests');
      isCancelled = true;
    };
  }, [isOpen, i18n.language, isOverseas]);

  /** ---------- URL sync 헬퍼 (Cognitive Complexity 분산) ---------- */

  // 빈 SDK 여부에 따라 카테고리 확장/축소 처리
  const expandOrCollapseForSdk = useCallback(
    (sdkType: string) => {
      if (isValidSDKCategory(sdkType) && isEmptySDK(sdkType)) {
        collapseAll();
      } else {
        expandOnly(sdkType);
      }
    },
    [collapseAll, expandOnly],
  );

  // 지역에서 사용 불가능한 SDK일 때 첫 번째 SDK로 리다이렉트
  const redirectToFirstAvailableSDK = useCallback(
    (currentPath: string) => {
      const enabledCategories = getEnabledCategoriesByRegion(isOverseas);
      const firstCategory = enabledCategories[0];
      if (!firstCategory) return;

      const firstSDKType = getSDKTypeByCategory(firstCategory.id);
      if (!firstSDKType) return;

      logger.info('Current SDK not available for region, navigating to first available:', {
        currentPath,
        firstSDKType,
        isOverseas,
      });
      setCurrentSDK(firstSDKType);
      expandOnly(firstCategory.id);
      ensureFeatureSelectedForSdk(firstSDKType);

      requestAnimationFrame(() => {
        const path = getSDKPath(firstSDKType);
        logger.info('Redirecting to first available SDK:', { path });
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        navigate(path);
      });
    },
    [isOverseas, setCurrentSDK, expandOnly, ensureFeatureSelectedForSdk, getSDKPath, navigate],
  );

  // 초기 로드 시 카테고리 확장 보장
  const handleInitialLoad = useCallback(
    (sdkType: string) => {
      logger.info('Initial load: ensuring category is expanded:', sdkType);
      expandOrCollapseForSdk(sdkType);
      if (isValidSDKCategory(sdkType) && isEmptySDK(sdkType)) {
        ensureFeatureSelectedForSdk(sdkType);
      }
      urlSyncInitializedRef.current = true;
    },
    [expandOrCollapseForSdk, ensureFeatureSelectedForSdk],
  );

  /** ---------- URL ↔ 상태 동기화 (✅ P0-4: Debounced to prevent race conditions) ---------- */
  // URL 변경 시에만 동기화 (selectedFeature 변경으로 인한 재실행 방지)
  useEffect(() => {
    if (Object.keys(features).length === 0 || categories.length === 0) {
      logger.info('Waiting for features/categories to load');
      return;
    }

    const pathToSDKType = getPathToMenuIdByRegion(isOverseas);
    const currentPath = location.pathname.replace(/^\/(?:en|ja)(\/|$)/, '/') || '/';
    const sdkType = pathToSDKType[currentPath];

    logger.info('URL sync:', {
      pathname: location.pathname,
      currentPath,
      sdkType,
      language: i18n.language,
      currentSelectedFeature: selectedFeature?.id,
      currentFeatureCategory: selectedFeature?.category,
    });

    // Clear SDK state for non-SDK pages (e.g., /license)
    const nonSDKPaths = ['/license'];
    if (nonSDKPaths.includes(currentPath)) {
      logger.info('Non-SDK page detected, clearing SDK state:', currentPath);
      setSelectedFeature(null);
      setCurrentSDK(null);
      collapseAll();
      return;
    }

    if (!sdkType) {
      redirectToFirstAvailableSDK(currentPath);
      return;
    }

    const isAlreadyCorrect =
      selectedFeature?.category === sdkType ||
      (isValidSDKCategory(sdkType) && isEmptySDK(sdkType) && selectedFeature?.id === sdkType) ||
      useAppStore.getState().currentSDK === sdkType;

    if (isAlreadyCorrect) {
      if (!urlSyncInitializedRef.current) {
        handleInitialLoad(sdkType);
      } else {
        logger.info('Feature and category already correct, skipping update');
      }
      return;
    }

    logger.info('URL sync: updating SDK selection', {
      from: selectedFeature?.category,
      to: sdkType,
      isEmptySDK: isValidSDKCategory(sdkType) && isEmptySDK(sdkType),
    });

    setCurrentSDK(sdkType);
    expandOrCollapseForSdk(sdkType);
    ensureFeatureSelectedForSdk(sdkType);
    urlSyncInitializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // URL과 언어 변경 시에만 실행 (selectedFeature 제외하여 무한 루프 방지)
    location.pathname,
    i18n.language,
    isOverseas,
    features,
    categories.length,
  ]);

  /** ---------- 높이 계산 (✅ P2-2: Using constants) ---------- */
  const recalcHeight = useCallback(() => {
    if (!sidebarRef.current) return;
    setFeatureListHeight(calcHeight(expandedCategories, sidebarRef.current.clientHeight));
  }, [expandedCategories]);

  // ✅ P1-4: Throttled resize handler to improve performance
  useEffect(() => {
    recalcHeight();

    // Throttle resize events to max once per 200ms
    const throttledResize = throttle(recalcHeight, 200);

    window.addEventListener('resize', throttledResize);
    return () => window.removeEventListener('resize', throttledResize);
  }, [recalcHeight]);

  /** ---------- 액션 ---------- */
  const handleFeatureSelect = useCallback(
    (feature: Feature) => {
      logger.info('handleFeatureSelect:', {
        id: feature.id,
        category: feature.category,
        title: feature.title,
      });

      const sdkType = getSDKTypeByCategory(feature.category);
      if (!sdkType) {
        logger.error('Failed to get SDK type from category:', feature.category);
        return;
      }

      // 상태 업데이트를 먼저 수행
      setSelectedFeature(feature);
      setCurrentSDK(sdkType);
      expandOnly(feature.category);

      // navigate는 다음 프레임에서 실행하여 URL sync와의 경쟁 조건 방지
      requestAnimationFrame(() => {
        const path = getSDKPath(sdkType);
        logger.info('Navigating to:', { sdkType, path, language: i18n.language });
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        navigate(path);
      });
    },
    [expandOnly, setSelectedFeature, setCurrentSDK, getSDKPath, i18n.language, navigate],
  );

  const handleEmptySDKSelect = useCallback(
    (categoryId: SDKType) => {
      const sdkType = getSDKTypeByCategory(categoryId);
      if (!sdkType) return;

      logger.info('handleEmptySDKSelect called:', {
        categoryId,
        sdkType,
        currentSelectedFeature: selectedFeature?.id,
        currentSelectedCategory: selectedFeature?.category,
      });

      // 상태 업데이트를 navigate보다 먼저 수행하여 URL sync와의 경쟁 조건 방지
      const virtualFeature = createVirtualFeature(sdkType);
      logger.info('Setting virtual feature:', virtualFeature);

      setCurrentSDK(sdkType);
      setSelectedFeature(virtualFeature);
      collapseAll();

      // 상태 업데이트 후 navigate (다음 프레임에서 실행)
      requestAnimationFrame(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        navigate(getSDKPath(sdkType));
      });
    },
    [collapseAll, getSDKPath, navigate, setCurrentSDK, setSelectedFeature, selectedFeature],
  );

  const toggleCategory = useCallback((categoryId: SDKType) => {
    setExpandedCategories(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        next[key] = key === categoryId ? !prev[key] : false;
      });
      return next;
    });
  }, []);

  const handleCategoryClick = useCallback(
    (categoryId: SDKType) => {
      if (isEmptySDK(categoryId)) {
        handleEmptySDKSelect(categoryId);
        return;
      }

      // 닫혀있던 상태에서 열릴 때만 첫 기능 자동 선택
      const wasExpanded = !!expandedCategories[categoryId];
      toggleCategory(categoryId);

      if (!wasExpanded) {
        const category = categories.find(cat => cat.id === categoryId);
        const firstFeature = category?.features?.[0];
        if (firstFeature) {
          const sdkType = getSDKTypeByCategory(categoryId);
          if (sdkType) {
            // 상태 업데이트를 먼저 수행
            setSelectedFeature(firstFeature);
            setCurrentSDK(sdkType);
            expandOnly(categoryId);

            // navigate는 다음 프레임에서 실행
            requestAnimationFrame(() => {
              const path = getSDKPath(sdkType);
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              navigate(path);
            });
          }
        }
      }
    },
    [
      expandedCategories,
      handleEmptySDKSelect,
      toggleCategory,
      categories,
      setSelectedFeature,
      setCurrentSDK,
      expandOnly,
      getSDKPath,
      navigate,
    ],
  );

  return {
    // 상태
    expandedCategories,
    features,
    categories,
    featureListHeight,
    selectedFeature,
    isLoading,
    error,

    // 참조
    sidebarRef,

    // 액션
    handleCategoryClick,
    handleFeatureSelect,
    collapseAll,

    // 유틸리티
    isEmptySDK,
    getSDKTypeByCategory,
  };
};
